import { useMemo } from "react";
import { View } from "@common/types/views/views";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ViewConfig } from "@lib/views";
import { useTypedFlags } from "@hooks/useTypedFlags";
import {
    getDefaultAccrualMatchView,
    getLegacyAccrualMatchView,
} from "./accrualMatchView";
import { createAccrualMatchViewConfig } from "./accrualMatchViewConfig";
import { AccrualMatchColumn, MatchItemType } from "./types";
import { useAccrualMatch } from "./useAccrualMatch";

export type AccrualMatchColumnsVariant = "workspaceMatched" | "legacy";

export interface AccrualMatchViewDefaults {
    /**
     * Column set the drawer defaults to (and that ColumnSettings' Reset /
     * "changed from default" indicator compare against).
     */
    defaultView: View;
    /**
     * Config used to reconcile the cached view (`mergeCachedView`) and to
     * drive ColumnSettings' toggle list. For the workspace-matched drawer it
     * excludes columns that don't apply to the current entity/workspace (see
     * below), so it is intentionally not the full render config the table uses.
     */
    viewConfig: ViewConfig<any, string, string>;
}

interface AccrualMatchViewDefaultsInput {
    columnsVariant: AccrualMatchColumnsVariant;
    ledgerBasis: AccountingLedgerBasis;
    isGlFirst: boolean;
    /**
     * Invoices label the cash/accrual columns Income account and Receivables
     * account. Bills label them Expense account and Payables account. Other
     * types keep Cash ledger and Accrual ledger.
     */
    matchItemType?: MatchItemType | null;
    /** The `schedules` flag; when off the Schedule column is dropped everywhere. */
    isSchedulesEnabled?: boolean;
}

/**
 * Pure builder for the accrual-match drawer's default view and the config that
 * reconciles it against the cache (PRO-12048).
 *
 * For the workspace-matched drawer, columns that don't apply to the current
 * entity/workspace are dropped from `viewConfig` — the Accrual ledger column
 * when the source transaction's entity is cash-only, the Category column for a
 * GL-first workspace, and the legacy basis-swapped Account column. When the
 * transaction side has an accrual ledger, that column is required: it is
 * forced into cached views and cannot be hidden from the cog. Cash and accrual stay the same
 * columns; invoice and bill matches only retitle them (and their cog labels).
 * Leaving Account in the config offered a second "Accrual ledger" / "Cash
 * ledger" column beside those titles. The view cache is shared across every
 * entity in the workspace, and `mergeCachedView` keeps any cached column still
 * known to the config; omitting the inapplicable ones here stops a column
 * cached while a different entity was open from leaking into this one's
 * drawer, and keeps ColumnSettings from offering a toggle the entity can't use.
 */
export function buildAccrualMatchViewDefaults({
    columnsVariant,
    ledgerBasis,
    isGlFirst,
    matchItemType = null,
    isSchedulesEnabled = false,
}: AccrualMatchViewDefaultsInput): AccrualMatchViewDefaults {
    // `ledgerBasis` is `accruals` exactly when the source transaction's entity
    // has an accrual ledger (see TransactionMatchDrawer).
    const hasAccrualLedger = ledgerBasis === AccountingLedgerBasis.accruals;

    const defaultView =
        columnsVariant === "workspaceMatched"
            ? getDefaultAccrualMatchView({
                  isGlFirst,
                  hasAccrualLedger,
                  isSchedulesEnabled,
              })
            : getLegacyAccrualMatchView({ isSchedulesEnabled });

    const config = createAccrualMatchViewConfig(matchItemType, {
        ledgerBasis,
        isGlFirst,
    });

    const excludedColumnIds = new Set<string>();
    if (!isSchedulesEnabled) {
        excludedColumnIds.add(AccrualMatchColumn.SCHEDULE);
    }
    if (columnsVariant === "workspaceMatched") {
        excludedColumnIds.add(AccrualMatchColumn.ACCOUNT);
    }
    if (columnsVariant === "workspaceMatched" && isGlFirst) {
        excludedColumnIds.add(AccrualMatchColumn.CATEGORY);
    }
    if (columnsVariant === "workspaceMatched" && !hasAccrualLedger) {
        excludedColumnIds.add(AccrualMatchColumn.ACCRUAL_LEDGER);
    }
    if (excludedColumnIds.size === 0) {
        return { defaultView, viewConfig: config };
    }

    const columns = Object.fromEntries(
        Object.entries(config.columns).filter(
            ([id]) => !excludedColumnIds.has(id),
        ),
    );
    return { defaultView, viewConfig: { ...config, columns } };
}

/**
 * Single source of truth for the accrual-match drawer's default view and merge
 * config. Shared by `AccrualMatchViewProvider` (which produces the actual table
 * default) and `AccrualMatchColumnSettings` (which drives Reset and the
 * "changed from default" indicator) so the two can never silently diverge.
 */
export function useAccrualMatchViewDefaults(
    columnsVariant: AccrualMatchColumnsVariant,
    matchItemType?: MatchItemType | null,
): AccrualMatchViewDefaults {
    const { ledgerBasis, isGlFirst } = useAccrualMatch();
    const { schedules: isSchedulesEnabled } = useTypedFlags();
    return useMemo(
        () =>
            buildAccrualMatchViewDefaults({
                columnsVariant,
                ledgerBasis,
                isGlFirst,
                matchItemType,
                isSchedulesEnabled,
            }),
        [
            columnsVariant,
            ledgerBasis,
            isGlFirst,
            matchItemType,
            isSchedulesEnabled,
        ],
    );
}
